import React, { useState, useEffect, useRef } from 'react';
import { LoginComponent, RegisterComponent } from '../components/auth/AuthComponents';
import ForgotPasswordComponent from '../components/auth/ForgotPasswordComponent';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';
import useIsMobile from '../hooks/useIsMobile';
import { ThemeToggle } from '../contexts/ThemeContext';
import DocxsAiLogo from '../components/shared/DocxsAiLogo';

const AUTH_PAGE_IMAGE = '/authpage.jpg';

const AuthPage = () => {
  const [mode, setMode] = useState('signin'); // 'signin', 'signup', or 'forgot-password'
  const location = useLocation();
  const toastShown = useRef(false);
  const isMobile = useIsMobile(1024); // Use 1024px as the breakpoint

  // Listen for custom event to switch auth mode (e.g., from forgot password link)
  useEffect(() => {
    const handler = (e) => {
      if (e.detail && e.detail.mode) {
        setMode(e.detail.mode);
      }
    };
    window.addEventListener('docxsai-switch-auth-mode', handler);
    return () => window.removeEventListener('docxsai-switch-auth-mode', handler);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verified = params.get('verified');
    if (!toastShown.current && (verified === '1' || verified === '0')) {
      if (verified === '1') {
        toast.success('Email verified! You can now sign in.');
      } else if (verified === '0') {
        toast.error('Verification link is invalid or expired.');
      }
      toastShown.current = true;
      // Remove the query param from the URL after showing the toast
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.search]);

  const handleModeSwitch = (newMode) => {
    setMode(newMode);
  };

  const handleForgotPasswordSuccess = () => {
    // Keep the user on the forgot password success state
    // They can use the "Back to login" button to return
  };

  const renderAuthComponent = () => {
    switch (mode) {
      case 'signin':
        return (
          <LoginComponent 
            isPage={true} 
            onSwitchToRegister={handleModeSwitch}
          />
        );
      case 'signup':
        return (
          <RegisterComponent 
            isPage={true} 
            onSwitchToLogin={() => setMode('signin')}
          />
        );
      case 'forgot-password':
        return (
          <ForgotPasswordComponent
            onBack={() => setMode('signin')}
            onSuccess={handleForgotPasswordSuccess}
          />
        );
      default:
        return (
          <LoginComponent 
            isPage={true} 
            onSwitchToRegister={handleModeSwitch}
          />
        );
    }
  };

  const renderToggleText = () => {
    if (mode === 'forgot-password') {
      return null; // The ForgotPasswordComponent handles its own navigation
    }
    
    return (
      <div className="mt-6 text-[15px] text-[var(--color-text-muted)]">
        {mode === 'signin' ? (
          <>Don't have an account?{' '}
            <span
              className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] cursor-pointer font-medium"
              onClick={() => setMode('signup')}
            >
              Sign Up
            </span>
          </>
        ) : (
          <>Already have an account?{' '}
            <span
              className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] cursor-pointer font-medium"
              onClick={() => setMode('signin')}
            >
              Sign In
            </span>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen flex bg-[var(--color-bg)]">
      <div className={`flex w-full ${isMobile ? 'flex-col items-center justify-center' : 'flex-row items-stretch'}`}>
        {/* Form Section */}
        <div
          className={`relative flex-1 flex flex-col justify-center bg-[var(--color-bg)] ${
            isMobile
              ? 'items-center py-12 px-4 w-full'
              : 'py-12 px-8 flex-shrink-0 max-w-md'
          }`}
        >
          <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-[var(--color-text-muted)] hover:text-[var(--color-primary)] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Home
            </Link>
            <ThemeToggle compact />
          </div>
          <div className="flex justify-center mb-8">
            <DocxsAiLogo className="h-16 w-auto" />
          </div>
          <div style={{ width: '100%', maxWidth: 400 }}>
            {renderAuthComponent()}
          </div>
          {renderToggleText()}
        </div>
        {/* Image Section - only show on desktop */}
        {!isMobile && (
          <div className="flex-1 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${AUTH_PAGE_IMAGE})` }} />
        )}
      </div>
    </div>
  );
};

export default AuthPage;
